import { useState, useEffect, useRef } from 'react'
import '../monacoSetup'
import {
  Box,
  HStack,
  Text,
  useColorModeValue,
  Badge,
  Flex,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react'
import { CopyIcon } from '@chakra-ui/icons'
import * as monaco from 'monaco-editor'

// Assembly Editor Component using Monaco
interface AssemblyEditorProps {
  assemblyLines: AssemblyLine[]
  selectedSourceLine: number | null
  selectedAssemblyLine: number | null
  onLineClick: (assemblyIndex: number) => void
}

function AssemblyEditor({ assemblyLines, selectedSourceLine, selectedAssemblyLine, onLineClick }: AssemblyEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const editorInstanceRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const decorationIdsRef = useRef<string[]>([])

  // Theme support
  const currentTheme = useColorModeValue('vs', 'vs-dark')

  const getAssemblyLinesForSourceLine = (sourceLineNumber: number) => {
    const assemblyIndices: number[] = []
    assemblyLines.forEach((line, index) => {
      if (line?.source && line.source.line === sourceLineNumber) {
        assemblyIndices.push(index)
      }
    })
    return assemblyIndices
  }

  // Convert assembly lines to text
  const assemblyText = assemblyLines.map(line => line.text).join('\n')

  // Initialize Monaco editor
  useEffect(() => {
    if (editorRef.current && !editorInstanceRef.current) {
      editorInstanceRef.current = monaco.editor.create(editorRef.current, {
        value: assemblyText,
        language: 'plaintext',
        theme: currentTheme,
        automaticLayout: true,
        readOnly: true,
        minimap: { 
          enabled: true,
          side: 'right',
          showSlider: 'always',
          renderCharacters: false,
          maxColumn: 80,
          scale: 1
        },
        scrollbar: {
          vertical: 'visible',
          horizontal: 'visible',
          verticalScrollbarSize: 14,
          horizontalScrollbarSize: 14,
        },
        overviewRulerLanes: 2,
        overviewRulerBorder: true,
        fontSize: 12,
        lineNumbers: 'on',
        wordWrap: 'off',
        folding: false,
        renderLineHighlight: 'line',
        glyphMargin: false,
        lineDecorationsWidth: 10,
        lineNumbersMinChars: 3
      })

      // Add click handler
      editorInstanceRef.current.onMouseDown((e) => {
        if (e.target.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS || 
            e.target.type === monaco.editor.MouseTargetType.CONTENT_TEXT) {
          const position = e.target.position
          if (position) {
            onLineClick(position.lineNumber - 1) // Convert to 0-based index
          }
        }
      })

      // Force layout after a small delay to ensure proper rendering
      setTimeout(() => {
        if (editorInstanceRef.current) {
          editorInstanceRef.current.layout()
        }
      }, 100)
    }

    return () => {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.dispose()
        editorInstanceRef.current = null
      }
    }
  }, [currentTheme])

  // Handle theme changes
  useEffect(() => {
    if (editorInstanceRef.current) {
      monaco.editor.setTheme(currentTheme)
    }
  }, [currentTheme])

  // Update editor content when assembly changes
  useEffect(() => {
    if (editorInstanceRef.current && assemblyText) {
      editorInstanceRef.current.setValue(assemblyText)
    }
  }, [assemblyText])

  // Handle highlighting decorations
  useEffect(() => {
    if (editorInstanceRef.current) {
      const decorations: monaco.editor.IModelDeltaDecoration[] = []

      // Highlight selected assembly line (orange)
      if (selectedAssemblyLine !== null) {
        decorations.push({
          range: new monaco.Range(selectedAssemblyLine + 1, 1, selectedAssemblyLine + 1, 1),
          options: {
            isWholeLine: true,
            className: 'assembly-selected-line',
            overviewRuler: {
              color: 'rgba(249, 115, 22, 0.8)',
              position: monaco.editor.OverviewRulerLane.Full
            },
            minimap: {
              color: 'rgba(249, 115, 22, 0.9)',
              position: monaco.editor.MinimapPosition.Inline
            }
          }
        })
      }

      // Highlight lines corresponding to selected source line (blue)
      if (selectedSourceLine !== null) {
        const highlightedAssemblyLines = getAssemblyLinesForSourceLine(selectedSourceLine + 1)
        highlightedAssemblyLines.forEach(assemblyIndex => {
          decorations.push({
            range: new monaco.Range(assemblyIndex + 1, 1, assemblyIndex + 1, 1),
            options: {
              isWholeLine: true,
              className: 'assembly-highlighted-line',
              overviewRuler: {
                color: 'rgba(59, 130, 246, 0.8)',
                position: monaco.editor.OverviewRulerLane.Left
              },
              minimap: {
                color: 'rgba(59, 130, 246, 0.9)',
                position: monaco.editor.MinimapPosition.Inline
              }
            }
          })
        })
      }

      // Apply decorations
      decorationIdsRef.current = editorInstanceRef.current.deltaDecorations(decorationIdsRef.current, decorations)
      
      // Ensure theme remains correct after decoration changes
      monaco.editor.setTheme(currentTheme)
    }
  }, [selectedSourceLine, selectedAssemblyLine, assemblyLines, currentTheme])

  return (
    <Box 
      h="100%" 
      w="100%" 
      ref={editorRef}
      position="relative"
    />
  )
}





interface AssemblyLine {
  text: string
  source?: {
    file: number
    line: number
    column: number
  } | undefined
  labels?: Array<{
    name: string
    range: [number, number]
  }> | undefined
}

interface AssemblyViewerProps {
  assemblyText: string
  sourceCode: string
  onCopy?: (text: string) => void
  assemblyOnlyMode?: boolean
  // External state for assembly-only mode coordination
  externalSelectedSourceLine?: number | null
  externalSelectedAssemblyLine?: number | null
  onSourceLineSelect?: (lineNumber: number | null) => void
  onAssemblyLineSelect?: (lineNumber: number | null, sourceLineNumber: number | null) => void
}



export default function AssemblyViewer({ 
  assemblyText, 
  sourceCode, 
  onCopy, 
  assemblyOnlyMode = false,
  externalSelectedSourceLine,
  externalSelectedAssemblyLine,
  onAssemblyLineSelect
}: AssemblyViewerProps) {
  const [assemblyLines, setAssemblyLines] = useState<AssemblyLine[]>([])
  const [selectedLine, setSelectedLine] = useState<number | null>(null)
  const [selectedSourceLine, setSelectedSourceLine] = useState<number | null>(null)

  const toast = useToast()

  // Theme colors
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const bgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.800', 'gray.100')

  // Responsive design
  const isCompact = useBreakpointValue({ base: true, lg: false })

  // Parse assembly text into structured format
  useEffect(() => {
    if (!assemblyText) {
      return
    }

    const parsedLines: AssemblyLine[] = []

    // Try to parse as JSON first (Godbolt API response)
    try {
      const jsonData = JSON.parse(assemblyText)
      
      if (jsonData.asm && Array.isArray(jsonData.asm)) {
        // This is a Godbolt API response
        jsonData.asm.forEach((asmItem: any) => {
          if (asmItem.text) {
            const line: AssemblyLine = {
              text: asmItem.text,
              source: asmItem.source ? {
                file: asmItem.source.file === null ? 1 : parseInt(asmItem.source.file) || 1,
                line: asmItem.source.line || 0,
                column: 0
              } : undefined,
              labels: []
            }
            parsedLines.push(line)
          }
        })
        setAssemblyLines(parsedLines)
        return
      }
    } catch (e) {
      // Not JSON, this should not happen if Godbolt API is working correctly
      console.error('AssemblyViewer received non-JSON data:', e)
      setAssemblyLines([])
      return
    }

    // If we reach here, it means we didn't get a valid Godbolt JSON response
    // Don't create any heuristic mapping - only use Godbolt's source mapping
    setAssemblyLines([])
  }, [assemblyText, sourceCode])



  const getSourceLineForAssemblyLine = (assemblyIndex: number) => {
    // Find the closest source line information
    for (let i = assemblyIndex; i >= 0; i--) {
      const line = assemblyLines[i]
      if (line?.source) {
        return line.source.line
      }
    }
    return null
  }



  const handleAssemblyLineClick = (assemblyIndex: number) => {
    const sourceLine = getSourceLineForAssemblyLine(assemblyIndex)
    
    if (assemblyOnlyMode && onAssemblyLineSelect) {
      // In assembly-only mode, use external state management
      const newSelection = externalSelectedAssemblyLine === assemblyIndex ? null : assemblyIndex
      // When clearing assembly selection, also clear source selection
      const newSourceSelection = newSelection === null ? null : sourceLine
      onAssemblyLineSelect(newSelection, newSourceSelection)
    } else {
      // Normal mode - use internal state
      setSelectedLine(selectedLine === assemblyIndex ? null : assemblyIndex)
      setSelectedSourceLine(null) // Clear source selection
    }
  }



  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(assemblyText)
      toast({
        title: 'Copied!',
        description: 'Assembly code copied to clipboard.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
      onCopy?.(assemblyText)
    } catch (err) {
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy to clipboard.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <Box 
      border="1px" 
      borderColor={borderColor} 
      borderRadius="md" 
      bg={bgColor}
      h="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Box 
        p={3} 
        borderBottom="1px" 
        borderColor={borderColor}
        flexShrink={0}
      >
        <Flex 
          justify="space-between" 
          align="center"
          direction={isCompact ? 'column' : 'row'}
          gap={isCompact ? 2 : 0}
        >
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" color={textColor}>Assembly Output</Text>
            <Badge colorScheme="green" variant="subtle">
              {assemblyLines.length} lines
            </Badge>
          </HStack>
          <HStack spacing={2}>
            <Badge 
              colorScheme="blue" 
              variant="subtle" 
              cursor="pointer"
              onClick={copyToClipboard}
              _hover={{ bg: 'blue.100' }}
            >
              <CopyIcon mr={1} boxSize={3} />
              Copy
            </Badge>
          </HStack>
        </Flex>
      </Box>

      {/* Content - Assembly Editor */}
      <Box 
        flex={1}
        minH={0}
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Assembly Panel Header */}
        <Box 
          p={2} 
          bg={useColorModeValue('gray.100', 'gray.800')} 
          borderBottom="1px" 
          borderColor={borderColor} 
          flexShrink={0}
        >
          <Text fontSize="xs" fontWeight="semibold" color={textColor}>Assembly</Text>
        </Box>
        
        {/* Monaco Editor Container */}
        <Box flex={1} minH={0}>
          {assemblyLines.length === 0 ? (
            <Box textAlign="center" py={8} color="gray.500">
              <Text color={textColor}>No assembly output available</Text>
              <Text fontSize="sm">Generate assembly first or check for compilation errors</Text>
            </Box>
          ) : (
            <AssemblyEditor
              assemblyLines={assemblyLines}
              selectedSourceLine={assemblyOnlyMode ? (externalSelectedSourceLine ?? null) : selectedSourceLine}
              selectedAssemblyLine={assemblyOnlyMode ? (externalSelectedAssemblyLine ?? null) : selectedLine}
              onLineClick={handleAssemblyLineClick}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
} 